import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { fetchAcademyOnce } from './academyDb';
import {
  subscribeMembers,
  subscribeAllProgress,
  fetchMemberKeys,
  createMember,
  setMemberActive,
  regenerateKey,
  deleteMember,
  flattenProgress,
  setMemberAccess,
  accessKey,
} from './academyMembersData';
import { exportMemberSubmissions } from './exportSubmissions';
import './AcademyMembers.css';

const IconLock = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);
const IconCheck = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M20 6 9 17l-5-5" />
  </svg>
);

// Cover image for a course: explicit cover, else first lesson's preview.
const courseCover = (course) =>
  course?.cover || (course?.lessons && course.lessons[0] && course.lessons[0].preview) || '';

// Infer a lesson's single type (mirror of AcademyContext.lessonType).
const lessonType = (lesson) => {
  if (lesson?.type === 'video' || lesson?.type === 'practice' || lesson?.type === 'quiz') return lesson.type;
  if (lesson?.practiceLab) return 'practice';
  if (lesson?.quiz) return 'quiz';
  return 'video';
};

const isStepDone = (entry, type) => {
  if (!entry) return false;
  if (type === 'practice') return !!entry.practiceDone;
  if (type === 'quiz') return !!entry.quizDone;
  return !!entry.watched;
};

const PRACTICE_MODE_LABEL = {
  easy: 'Easy Mode',
  hard: 'Hard Mode',
  subject: 'Subject Line Mode',
  salesgod: 'SalesGod Mode',
};

const fmtDate = (ts) => {
  if (!ts) return '—';
  const d = new Date(ts);
  return d.toLocaleDateString('de-DE', { day: '2-digit', month: 'short', year: 'numeric' });
};

const fmtDateTime = (ts) => {
  if (!ts) return '—';
  const d = new Date(ts);
  return d.toLocaleString('de-DE', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const relTime = (ts) => {
  if (!ts) return 'Nie aktiv';
  const diff = Date.now() - ts;
  const min = Math.floor(diff / 60000);
  if (min < 1) return 'Gerade aktiv';
  if (min < 60) return `vor ${min} Min.`;
  const h = Math.floor(min / 60);
  if (h < 24) return `vor ${h} Std.`;
  const d = Math.floor(h / 24);
  if (d < 30) return `vor ${d} Tg.`;
  return fmtDate(ts);
};

// Totals computed from the live content tree.
const computeTotals = (topics) => {
  let videos = 0;
  let steps = 0;
  let courses = 0;
  (topics || []).forEach((t) => {
    (t.courses || []).forEach((c) => {
      courses += 1;
      (c.lessons || []).forEach((l) => {
        steps += 1;
        if (lessonType(l) === 'video') videos += 1;
      });
    });
  });
  return { videos, steps, courses };
};

// Per-member stats + collected submissions, scoped to the courses the member
// has access to. Totals reflect that scope (so % is meaningful).
const computeMemberData = (topics, flat, access) => {
  let videosWatched = 0;
  let stepsDone = 0;
  let coursesDone = 0;
  let badges = 0;
  let totalVideos = 0;
  let totalSteps = 0;
  let totalCourses = 0;
  let unlockedCourses = 0;
  const submissions = [];
  (topics || []).forEach((t) => {
    (t.courses || []).forEach((c) => {
      if (!(access && access[`${t.id}__${c.id}`])) return; // locked → not counted
      unlockedCourses += 1;
      totalCourses += 1;
      const lessons = c.lessons || [];
      let courseDone = lessons.length > 0;
      lessons.forEach((l) => {
        totalSteps += 1;
        if (lessonType(l) === 'video') totalVideos += 1;
        const type = lessonType(l);
        const entry = flat[`${t.id}/${c.id}/${l.id}`];
        const done = isStepDone(entry, type);
        if (done) {
          stepsDone += 1;
          if (type === 'video') videosWatched += 1;
        } else {
          courseDone = false;
        }
        if (entry && entry.submission && (type === 'practice' || type === 'quiz')) {
          submissions.push({
            type,
            topicTitle: t.title,
            courseTitle: c.title,
            lessonTitle: l.title || l.id,
            submission: entry.submission,
            submittedAt: entry.submittedAt || 0,
          });
        }
      });
      if (courseDone) {
        coursesDone += 1;
        if (c.badge && c.badge.id) badges += 1;
      }
    });
  });
  submissions.sort((a, b) => (b.submittedAt || 0) - (a.submittedAt || 0));
  return {
    videosWatched,
    stepsDone,
    coursesDone,
    badges,
    submissions,
    totalVideos,
    totalSteps,
    totalCourses,
    unlockedCourses,
  };
};

// Renders modal content into document.body so the fixed overlay escapes the
// dashboard's animated, overflow-hidden page container (otherwise the modal
// gets clipped/cut off relative to that container instead of the viewport).
// Portal renders to <body>, outside the .am-wrap scope — re-apply the token
// scope so CSS variables (colors, borders) resolve inside modals.
const ModalPortal = ({ children }) =>
  createPortal(<div className="am-portal">{children}</div>, document.body);

// ── Submission rendering ──
const SubmissionView = ({ type, submission }) => {
  if (type === 'quiz') {
    const answers = submission.answers || [];
    return (
      <div className="am-sub-body">
        {answers.map((a, i) => (
          <div key={i} className="am-sub-q">
            <div className="am-sub-q-head">
              <span className="am-sub-q-no">{i + 1}</span>
              <span className="am-sub-q-text">{a.question}</span>
              {a.correct === true && <span className="am-tag am-tag-ok">Richtig</span>}
              {a.correct === false && <span className="am-tag am-tag-bad">Falsch</span>}
            </div>
            {a.type === 'multiple-choice' && (
              <p className="am-sub-a">{a.choiceText || `Option ${(a.choiceIndex ?? 0) + 1}`}</p>
            )}
            {a.type === 'text' && <p className="am-sub-a am-sub-pre">{a.text}</p>}
            {a.type === 'email' && (
              <div className="am-sub-email">
                <p className="am-sub-a"><strong>Betreff:</strong> {a.subject}</p>
                <p className="am-sub-a am-sub-pre">{a.body}</p>
              </div>
            )}
          </div>
        ))}
      </div>
    );
  }

  // Practice lab variants. The subject line is rendered in the email header
  // (see EmailWindow), so the body here only carries the message content.
  const mode = submission.mode;
  return (
    <div className="am-sub-body">
      {mode === 'easy' &&
        (submission.blocks || []).map((b, i) => (
          <div key={i} className="am-sub-block">
            <span className="am-sub-block-label">{b.label}</span>
            <p className="am-sub-a am-sub-pre">{b.value || '—'}</p>
          </div>
        ))}
      {mode === 'hard' && <p className="am-sub-a am-sub-pre">{submission.body || '—'}</p>}
      {mode === 'subject' && (
        <ol className="am-sub-subjects">
          {(submission.subjects || []).map((s, i) => (
            <li key={i}>{s || '—'}</li>
          ))}
        </ol>
      )}
      {mode === 'salesgod' && <pre className="am-sub-template">{submission.template || '—'}</pre>}
    </div>
  );
};

// Derives an email-style subject + tag for a collected submission so each entry
// can be presented as a real e-mail in the member's "inbox".
const describeSubmission = (s) => {
  if (s.type === 'quiz') {
    return { tag: 'Mini-Quiz', subject: `Quiz · ${s.lessonTitle}` };
  }
  const mode = s.submission?.mode;
  const tag = `Practice Lab · ${PRACTICE_MODE_LABEL[mode] || ''}`.trim();
  if (mode === 'easy' || mode === 'hard') {
    return { tag, subject: s.submission.subject?.trim() || '(Kein Betreff)' };
  }
  if (mode === 'subject') return { tag, subject: 'Betreffzeilen-Übung' };
  if (mode === 'salesgod') return { tag, subject: 'SalesGod Vorlage' };
  return { tag, subject: 'Einreichung' };
};

// One submission rendered as an e-mail window: window bar, subject line,
// sender/recipient meta and the message body.
const EmailWindow = ({ entry, member }) => {
  const { tag, subject } = describeSubmission(entry);
  const initial = (member.name || member.email || '·').slice(0, 1).toUpperCase();
  return (
    <article className="am-mail">
      <div className="am-mail-bar">
        <span className="am-mail-dots" aria-hidden="true">
          <i /><i /><i />
        </span>
        <span className={`am-mail-tag am-mail-tag-${entry.type}`}>{tag}</span>
        <span className="am-mail-date">{fmtDateTime(entry.submittedAt)}</span>
      </div>
      <div className="am-mail-head">
        <h4 className="am-mail-subject">{subject}</h4>
        <div className="am-mail-meta">
          <span className="am-mail-avatar">{initial}</span>
          <div className="am-mail-id">
            <span className="am-mail-from">
              <strong>{member.name || 'Mitglied'}</strong>
              {member.email && <span className="am-mail-email">&lt;{member.email}&gt;</span>}
            </span>
            <span className="am-mail-loc">
              {entry.topicTitle} <span className="am-mail-loc-sep">›</span> {entry.courseTitle}{' '}
              <span className="am-mail-loc-sep">›</span> {entry.lessonTitle}
            </span>
          </div>
        </div>
      </div>
      <div className="am-mail-body">
        <SubmissionView type={entry.type} submission={entry.submission} />
      </div>
    </article>
  );
};

// Inbox-style modal listing all submissions of a member as e-mail windows.
// Rendered through ModalPortal so it escapes the dashboard's overflow:hidden
// page container instead of being clipped inside the row.
const SubmissionsModal = ({ member, submissions, onClose }) => {
  const count = submissions.length;
  const [exportHint, setExportHint] = useState('');

  const handleExport = () => {
    const ok = exportMemberSubmissions(member, submissions);
    setExportHint(
      ok
        ? ''
        : 'Pop-up wurde blockiert. Bitte erlaube Pop-ups für diese Seite und versuche es erneut.'
    );
  };

  return (
    <ModalPortal>
      <div className="am-overlay" onClick={onClose} role="presentation">
        <div className="am-modal am-modal-inbox" onClick={(e) => e.stopPropagation()}>
          <div className="am-inbox-head">
            <div className="am-inbox-headtext">
              <h3 className="am-modal-title">Postfach · {member.name || member.id}</h3>
              <p className="am-modal-sub">
                {count} {count === 1 ? 'Einreichung' : 'Einreichungen'} aus Practice Lab &amp; Mini-Quiz
              </p>
            </div>
            <button type="button" className="am-inbox-close" onClick={onClose} aria-label="Schließen">
              ×
            </button>
          </div>
          <div className="am-inbox-body">
            {submissions.map((entry, i) => (
              <EmailWindow key={i} entry={entry} member={member} />
            ))}
          </div>
          {exportHint && <p className="am-inbox-hint">{exportHint}</p>}
          <div className="am-modal-actions">
            <button
              type="button"
              className="am-btn am-btn-accent"
              onClick={handleExport}
              disabled={count === 0}
            >
              Als PDF exportieren / teilen
            </button>
            <button type="button" className="am-btn am-btn-ghost" onClick={onClose}>
              Schließen
            </button>
          </div>
        </div>
      </div>
    </ModalPortal>
  );
};

const CreateModal = ({ onClose, onCreated }) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError('');
    try {
      const { id, key } = await createMember({ name, email, note });
      onCreated({ id, key, name: name.trim() || 'Neues Mitglied' });
    } catch (err) {
      setError(err.message || 'Anlegen fehlgeschlagen.');
      setBusy(false);
    }
  };

  return (
    <ModalPortal>
    <div className="am-overlay" onClick={onClose} role="presentation">
      <div className="am-modal" onClick={(e) => e.stopPropagation()}>
        <h3 className="am-modal-title">Neues Academy-Mitglied</h3>
        <p className="am-modal-sub">
          Alles optional – es reicht der automatisch generierte Secret Key. Lässt du Name & E-Mail leer,
          wird das Mitglied beim ersten Login selbst danach gefragt.
        </p>
        <form onSubmit={submit} className="am-modal-form">
          <label className="am-field">
            <span className="am-field-label">Name (optional)</span>
            <input
              className="am-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Max Mustermann"
              autoFocus
            />
          </label>
          <label className="am-field">
            <span className="am-field-label">E-Mail (optional)</span>
            <input
              className="am-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="max@firma.de"
            />
          </label>
          <label className="am-field">
            <span className="am-field-label">Notiz (optional)</span>
            <textarea
              className="am-input am-textarea"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="z. B. Tarif, Coach, Kontext …"
              rows={3}
            />
          </label>
          {error && <p className="am-error">{error}</p>}
          <div className="am-modal-actions">
            <button type="button" className="am-btn am-btn-ghost" onClick={onClose} disabled={busy}>
              Abbrechen
            </button>
            <button type="submit" className="am-btn am-btn-primary" disabled={busy}>
              {busy ? 'Legt an…' : 'Mitglied anlegen'}
            </button>
          </div>
        </form>
      </div>
    </div>
    </ModalPortal>
  );
};

const KeyRevealModal = ({ data, onClose }) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(data.key);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      /* ignore */
    }
  };
  return (
    <ModalPortal>
    <div className="am-overlay" onClick={onClose} role="presentation">
      <div className="am-modal am-modal-key" onClick={(e) => e.stopPropagation()}>
        <h3 className="am-modal-title">Secret Key für {data.name}</h3>
        <p className="am-modal-sub">
          Gib diesen Key an das Mitglied weiter. Er ist der einzige Zugang zur Academy.
        </p>
        <div className="am-keybox">
          <code className="am-keybox-code">{data.key}</code>
          <button type="button" className="am-btn am-btn-primary am-keybox-copy" onClick={copy}>
            {copied ? 'Kopiert ✓' : 'Kopieren'}
          </button>
        </div>
        <div className="am-modal-actions">
          <button type="button" className="am-btn am-btn-ghost" onClick={onClose}>
            Schließen
          </button>
        </div>
      </div>
    </div>
    </ModalPortal>
  );
};

const ConfirmModal = ({ data, onClose }) => {
  const [busy, setBusy] = useState(false);
  const confirm = async () => {
    setBusy(true);
    try {
      await data.onConfirm();
      onClose();
    } finally {
      setBusy(false);
    }
  };
  return (
    <ModalPortal>
    <div className="am-overlay" onClick={onClose} role="presentation">
      <div className="am-modal am-modal-danger" onClick={(e) => e.stopPropagation()}>
        <h3 className="am-modal-title">{data.title}</h3>
        {data.message && <p className="am-modal-sub">{data.message}</p>}
        <div className="am-modal-actions">
          <button type="button" className="am-btn am-btn-ghost" onClick={onClose} disabled={busy}>
            Abbrechen
          </button>
          <button type="button" className="am-btn am-btn-danger" onClick={confirm} disabled={busy}>
            {busy ? 'Bitte warten…' : data.confirmLabel || 'Bestätigen'}
          </button>
        </div>
      </div>
    </div>
    </ModalPortal>
  );
};

const Stat = ({ value, label }) => (
  <div className="am-stat">
    <span className="am-stat-value">{value}</span>
    <span className="am-stat-label">{label}</span>
  </div>
);

// Modal to grant/revoke course access per member. Courses are grouped by topic;
// each topic has a "select all" toggle. Empty selection = nothing unlocked.
const AccessModal = ({ member, topics, onClose }) => {
  const [selected, setSelected] = useState(() => ({ ...(member.access || {}) }));
  const [busy, setBusy] = useState(false);

  const toggle = (topicId, courseId) => {
    const k = accessKey(topicId, courseId);
    setSelected((prev) => {
      const next = { ...prev };
      if (next[k]) delete next[k];
      else next[k] = true;
      return next;
    });
  };

  const topicCourseKeys = (topic) => (topic.courses || []).map((c) => accessKey(topic.id, c.id));
  const allOn = (topic) => {
    const ks = topicCourseKeys(topic);
    return ks.length > 0 && ks.every((k) => selected[k]);
  };
  const toggleTopic = (topic) => {
    const ks = topicCourseKeys(topic);
    const turnOff = allOn(topic);
    setSelected((prev) => {
      const next = { ...prev };
      ks.forEach((k) => {
        if (turnOff) delete next[k];
        else next[k] = true;
      });
      return next;
    });
  };

  const count = Object.keys(selected).length;

  const save = async () => {
    setBusy(true);
    try {
      await setMemberAccess(member.id, selected);
      onClose();
    } finally {
      setBusy(false);
    }
  };

  return (
    <ModalPortal>
    <div className="am-overlay" onClick={onClose} role="presentation">
      <div className="am-modal am-modal-access" onClick={(e) => e.stopPropagation()}>
        <h3 className="am-modal-title">Kurse freischalten für {member.name || member.id}</h3>
        <p className="am-modal-sub">
          Nur freigeschaltete Kurse sind für dieses Mitglied sichtbar. Klicke einen Kurs, um ihn frei&shy;zuschalten oder wieder zu sperren. {count} freigeschaltet.
        </p>

        <div className="am-access-list">
          {topics.length === 0 && (
            <p className="am-access-empty">Noch keine Kurse im Katalog. Lege zuerst Inhalte im Academy-CMS an.</p>
          )}
          {topics.map((t) => {
            const ks = topicCourseKeys(t);
            const onCount = ks.filter((k) => selected[k]).length;
            return (
              <div key={t.id} className="am-access-topic">
                <div className="am-access-topic-head">
                  <span className="am-access-topic-thumb">
                    {t.image ? <img src={t.image} alt="" /> : <span className="am-access-topic-thumb-fb" />}
                  </span>
                  <div className="am-access-topic-info">
                    <span className="am-access-topic-title">{t.title || t.id}</span>
                    <span className="am-access-topic-count">
                      {onCount}/{ks.length} {ks.length === 1 ? 'Kurs' : 'Kurse'} freigeschaltet
                    </span>
                  </div>
                  <button type="button" className={`am-btn am-btn-mini ${allOn(t) ? '' : 'am-btn-accent'}`} onClick={() => toggleTopic(t)} disabled={ks.length === 0}>
                    {allOn(t) ? 'Ganzen Lehrgang sperren' : 'Ganzen Lehrgang freischalten'}
                  </button>
                </div>
                <div className="am-access-grid">
                  {(t.courses || []).length === 0 && (
                    <span className="am-access-nocourse">Keine Kurse in diesem Themengebiet.</span>
                  )}
                  {(t.courses || []).map((c) => {
                    const k = accessKey(t.id, c.id);
                    const on = !!selected[k];
                    const cover = courseCover(c);
                    return (
                      <button
                        type="button"
                        key={c.id}
                        className={`am-coursecard ${on ? 'is-on' : 'is-off'}`}
                        onClick={() => toggle(t.id, c.id)}
                        title={on ? 'Freigeschaltet – klicken zum Sperren' : 'Gesperrt – klicken zum Freischalten'}
                      >
                        <span className="am-coursecard-cover">
                          {cover ? <img src={cover} alt="" /> : <span className="am-coursecard-cover-fb" />}
                          <span className={`am-coursecard-state ${on ? 'is-on' : 'is-off'}`}>
                            {on ? <IconCheck /> : <IconLock />}
                            {on ? 'Frei' : 'Gesperrt'}
                          </span>
                        </span>
                        <span className="am-coursecard-body">
                          <span className="am-coursecard-title">{c.title || c.id}</span>
                          <span className="am-coursecard-meta">{(c.lessons || []).length} Lektionen</span>
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        <div className="am-modal-actions">
          <button type="button" className="am-btn am-btn-ghost" onClick={onClose} disabled={busy}>
            Abbrechen
          </button>
          <button type="button" className="am-btn am-btn-primary" onClick={save} disabled={busy}>
            {busy ? 'Speichert…' : 'Freischaltung speichern'}
          </button>
        </div>
      </div>
    </div>
    </ModalPortal>
  );
};

const MemberRow = ({ member, stats, topics, keyValue, onShowKey, onToggleActive, onRegenerate, onDelete, onAccess, onShowSubs }) => {
  const pct = stats.totalSteps > 0 ? Math.round((stats.stepsDone / stats.totalSteps) * 100) : 0;
  const inactive = member.active === false;
  const subs = stats.submissions || [];

  const unlocked = useMemo(() => {
    const acc = member.access || {};
    const list = [];
    (topics || []).forEach((t) =>
      (t.courses || []).forEach((c) => {
        if (acc[accessKey(t.id, c.id)]) {
          list.push({ id: `${t.id}__${c.id}`, title: c.title || c.id, topicTitle: t.title || t.id, cover: courseCover(c) });
        }
      })
    );
    return list;
  }, [topics, member.access]);

  return (
    <div className={`am-row ${inactive ? 'is-inactive' : ''}`}>
      <div className="am-row-main">
        <div className="am-row-id">
          <span className="am-row-avatar">{(member.name || '·').slice(0, 1).toUpperCase()}</span>
          <div className="am-row-idtext">
            <span className="am-row-name">
              {member.name || member.id}
              {inactive && <span className="am-pill am-pill-off">Deaktiviert</span>}
            </span>
            <span className="am-row-meta">
              {member.email || 'keine E-Mail'}
              {member.whatsapp ? ` · ${member.whatsapp}` : ''} · {relTime(stats.lastActiveAt)}
            </span>
          </div>
        </div>

        <div className="am-row-prog">
          <div className="am-row-bar">
            <div className="am-row-bar-fill" style={{ width: `${pct}%` }} />
          </div>
          <span className="am-row-pct">{pct}%</span>
        </div>
      </div>

      <div className="am-row-stats">
        <Stat value={stats.unlockedCourses} label="Freigeschaltet" />
        <Stat value={`${stats.videosWatched}/${stats.totalVideos}`} label="Videos" />
        <Stat value={`${stats.stepsDone}/${stats.totalSteps}`} label="Schritte" />
        <Stat value={`${stats.coursesDone}/${stats.totalCourses}`} label="Kurse" />
        <Stat value={stats.badges} label="Badges" />
        <Stat value={subs.length} label="Einreichungen" />
      </div>

      <div className="am-row-access">
        <span className="am-row-access-label">Freigeschaltete Kurse</span>
        {unlocked.length === 0 ? (
          <button type="button" className="am-row-access-empty" onClick={() => onAccess(member)}>
            Noch nichts freigeschaltet · jetzt auswählen
          </button>
        ) : (
          <button type="button" className="am-row-access-strip" onClick={() => onAccess(member)} title="Freischaltung bearbeiten">
            {unlocked.map((c) => (
              <span className="am-mini-course" key={c.id}>
                <span className="am-mini-course-cover">
                  {c.cover ? <img src={c.cover} alt="" /> : <span className="am-mini-course-cover-fb" />}
                </span>
                <span className="am-mini-course-title">{c.title}</span>
              </span>
            ))}
          </button>
        )}
      </div>

      <div className="am-row-actions">
        <code className="am-keyinline">{keyValue || '••••-••••-••••-••••'}</code>
        <button type="button" className="am-btn am-btn-mini am-btn-accent" onClick={() => onAccess(member)}>
          Kurse freischalten
        </button>
        <button
          type="button"
          className="am-btn am-btn-mini"
          onClick={() => onShowSubs(member, subs)}
          disabled={subs.length === 0}
        >
          {`Einreichungen (${subs.length})`}
        </button>
        <button type="button" className="am-btn am-btn-mini" onClick={() => onShowKey(member, keyValue)} disabled={!keyValue}>
          Key
        </button>
        <button type="button" className="am-btn am-btn-mini" onClick={() => onToggleActive(member)}>
          {inactive ? 'Aktivieren' : 'Sperren'}
        </button>
        <button type="button" className="am-btn am-btn-mini" onClick={() => onRegenerate(member)}>
          Neuer Key
        </button>
        <button type="button" className="am-btn am-btn-mini am-btn-danger-ghost" onClick={() => onDelete(member)}>
          Löschen
        </button>
      </div>
    </div>
  );
};

const AcademyMembers = () => {
  const [members, setMembers] = useState([]);
  const [progressMap, setProgressMap] = useState({});
  const [keys, setKeys] = useState({});
  const [topics, setTopics] = useState([]);
  const [keysError, setKeysError] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [revealKey, setRevealKey] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [accessFor, setAccessFor] = useState(null);
  const [subsFor, setSubsFor] = useState(null);
  const [query, setQuery] = useState('');

  useEffect(() => {
    const unsubM = subscribeMembers(setMembers);
    const unsubP = subscribeAllProgress(setProgressMap);
    return () => {
      unsubM();
      unsubP();
    };
  }, []);

  useEffect(() => {
    fetchAcademyOnce()
      .then((a) => setTopics(a.topics || []))
      .catch(() => setTopics([]));
  }, []);

  const loadKeys = useCallback(async () => {
    try {
      const k = await fetchMemberKeys();
      setKeys(k);
      setKeysError('');
    } catch (e) {
      setKeysError(e.message || 'Keys konnten nicht geladen werden.');
    }
  }, []);

  useEffect(() => {
    loadKeys();
  }, [loadKeys]);

  const totals = useMemo(() => computeTotals(topics), [topics]);

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return members
      .filter((m) => !q || (m.name || '').toLowerCase().includes(q) || (m.email || '').toLowerCase().includes(q))
      .map((m) => {
        const nested = (progressMap[m.id] && progressMap[m.id].lessons) || {};
        const flat = flattenProgress(nested);
        const stats = computeMemberData(topics, flat, m.access || {});
        stats.lastActiveAt = progressMap[m.id]?.lastActiveAt || 0;
        return { member: m, stats };
      });
  }, [members, progressMap, topics, query]);

  // Aggregate header stats.
  const summary = useMemo(() => {
    const active = members.filter((m) => m.active !== false).length;
    const totalDone = rows.reduce((acc, r) => acc + r.stats.stepsDone, 0);
    const totalBadges = rows.reduce((acc, r) => acc + r.stats.badges, 0);
    const totalSubs = rows.reduce((acc, r) => acc + (r.stats.submissions?.length || 0), 0);
    return { count: members.length, active, totalDone, totalBadges, totalSubs };
  }, [members, rows]);

  const onToggleActive = (m) => setMemberActive(m.id, m.active === false);

  const onRegenerate = (m) => {
    setConfirm({
      title: `Key für ${m.name || m.id} neu generieren?`,
      message: 'Der bisherige Key wird sofort ungültig. Das Mitglied braucht dann den neuen Key.',
      confirmLabel: 'Neuen Key erzeugen',
      onConfirm: async () => {
        const key = await regenerateKey(m.id);
        await loadKeys();
        setRevealKey({ name: m.name || m.id, key });
      },
    });
  };

  const onDelete = (m) => {
    setConfirm({
      title: `${m.name || m.id} löschen?`,
      message: 'Mitglied, Key und gesamter Fortschritt werden unwiderruflich gelöscht.',
      confirmLabel: 'Endgültig löschen',
      onConfirm: () => deleteMember(m.id),
    });
  };

  return (
    <div className="am-wrap">
      <header className="am-head">
        <div>
          <h1 className="am-title">Academy Members</h1>
          <p className="am-subtitle">Zugänge verwalten und sehen, wie fleißig die Mitglieder lernen.</p>
        </div>
        <button type="button" className="am-btn am-btn-primary" onClick={() => setCreateOpen(true)}>
          + Mitglied anlegen
        </button>
      </header>

      <div className="am-summary">
        <Stat value={summary.count} label="Mitglieder" />
        <Stat value={summary.active} label="Aktiv" />
        <Stat value={summary.totalDone} label="Schritte erledigt" />
        <Stat value={summary.totalBadges} label="Badges vergeben" />
        <Stat value={totals.courses} label="Kurse im Katalog" />
      </div>

      {keysError && (
        <div className="am-banner">
          {keysError}{' '}
          <button type="button" className="am-link" onClick={loadKeys}>
            Erneut versuchen
          </button>
        </div>
      )}

      <div className="am-toolbar">
        <input
          className="am-search"
          placeholder="Mitglied suchen…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>

      <div className="am-list">
        {rows.length === 0 ? (
          <div className="am-empty">
            <p className="am-empty-title">Noch keine Mitglieder</p>
            <p className="am-empty-sub">Lege das erste Academy-Mitglied an, um einen Zugang zu erstellen.</p>
          </div>
        ) : (
          rows.map(({ member, stats }) => (
            <MemberRow
              key={member.id}
              member={member}
              stats={stats}
              topics={topics}
              keyValue={keys[member.id]}
              onShowKey={(m, k) => setRevealKey({ name: m.name || m.id, key: k })}
              onToggleActive={onToggleActive}
              onRegenerate={onRegenerate}
              onDelete={onDelete}
              onAccess={setAccessFor}
              onShowSubs={(m, subs) => setSubsFor({ member: m, submissions: subs })}
            />
          ))
        )}
      </div>

      {createOpen && (
        <CreateModal
          onClose={() => setCreateOpen(false)}
          onCreated={({ key, name }) => {
            setCreateOpen(false);
            loadKeys();
            setRevealKey({ name, key });
          }}
        />
      )}
      {revealKey && <KeyRevealModal data={revealKey} onClose={() => setRevealKey(null)} />}
      {confirm && <ConfirmModal data={confirm} onClose={() => setConfirm(null)} />}
      {accessFor && (
        <AccessModal member={accessFor} topics={topics} onClose={() => setAccessFor(null)} />
      )}
      {subsFor && (
        <SubmissionsModal
          member={subsFor.member}
          submissions={subsFor.submissions}
          onClose={() => setSubsFor(null)}
        />
      )}
    </div>
  );
};

export default AcademyMembers;
